import { useContext, useState } from "react";
import moment from "moment";

import { AppContexts } from "../../providers";
import UIkit from "uikit";
import classnames from "classnames";
import { Image } from "uikit-react";

const wdFormat = "YYYY-MM-DD HH:mm:ss";

const requiredFields = ["first_name", "last_name", "email"];

const DOWS = [
  { id: 1, short: "Sun", label: "Sunday" },
  { id: 2, short: "Mon", label: "Monday" },
  { id: 3, short: "Tue", label: "Tuesday" },
  { id: 4, short: "Wed", label: "Wednesday" },
  { id: 5, short: "Thu", label: "Thursday" },
  { id: 6, short: "Fri", label: "Friday" },
  { id: 7, short: "Sat", label: "Saturday" },
];

const getDayPrefix = (dow) => `2001-01-0${dow}`;

const makeTimeOptions = (stepMins = 15) => {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += stepMins) {
      opts.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return opts;
};

const timeOptionsStart = makeTimeOptions(15);
const timeOptionsEnd = [...makeTimeOptions(15), "23:59"];

const hasTZ = (v) => /Z|[+-]\d{2}:\d{2}$/.test(String(v || "").trim());

const normalizeWallClock = (v) =>
  String(v || "")
    .trim()
    .replace("T", " ")
    .replace(/\.\d+/, "")
    .replace(/(Z|[+-]\d{2}:\d{2})$/, "")
    .trim();

const parseWd = (v) => {
  if (!v) return moment.invalid();
  const s0 = String(v).trim();
  if (hasTZ(s0)) return moment.parseZone(s0).local();

  const s = normalizeWallClock(s0);
  return moment(s, wdFormat, true);
};

const toStorage = (m) => m.format(wdFormat);

const localAt = (dateYYYYMMDD, hhmmss) =>
  moment(`${dateYYYYMMDD} ${hhmmss}`, wdFormat, true);

const hhmmToLabel = (hhmm) => moment(hhmm, "HH:mm").format("h:mma");

const TeamAdd = () => {
  let [errors, setErrors] = useState([]);
  let [isSaving, setIsSaving] = useState(false);

  let {
    recipient,
    setRecipient,
    createRecipient,
    updateRecipient,
    settings,
    isNew,
  } = useContext(AppContexts.InboxContext);

  const safeSchedule = Array.isArray(recipient?.schedule) ? recipient.schedule : [];

  const handleInput = (key, e) => {
    if (errors.includes(key)) {
      setErrors(errors.filter((f) => f !== key));
    }

    setRecipient({ ...recipient, [key]: e.target.value });
  };

  const getFilteredEndTimes = (startTime) => {
    if (!startTime) return timeOptionsEnd;

    const startMinutes =
      moment(startTime, "HH:mm").hours() * 60 +
      moment(startTime, "HH:mm").minutes();

    return timeOptionsEnd.filter((t) => {
      const endMinutes =
        moment(t, "HH:mm").hours() * 60 +
        moment(t, "HH:mm").minutes();
      return endMinutes > startMinutes;
    });
  };

  const setDayTime = (dow, key, hhmm) => {
    const currentSchedule = safeSchedule;
    const existing = currentSchedule.find((d) => +d?.weekday === +dow);

    let wd = existing
      ? { ...existing }
      : {
          weekday: dow,
          start_time: `2001-01-0${dow} 09:00:00`,
          end_time: `2001-01-0${dow} 17:00:00`,
        };

    const date = getDayPrefix(dow);
    const nextLocal = localAt(date, `${hhmm}:00`);

    if (key === "start_time") {
      const endLocal = parseWd(wd.end_time);
      wd.start_time = toStorage(nextLocal);

      if (!endLocal.isValid() || !endLocal.isAfter(nextLocal)) {
        wd.end_time = toStorage(nextLocal.clone().add(60, "minutes"));
      }
    } else {
      const startLocal = parseWd(wd.start_time);
      wd.end_time = toStorage(nextLocal);

      if (!startLocal.isValid() || !nextLocal.isAfter(startLocal)) {
        wd.start_time = toStorage(nextLocal.clone().subtract(60, "minutes"));
      }
    }

    const nextSchedule = existing
      ? currentSchedule.map((d) => (+d?.weekday === +dow ? wd : d))
      : [...currentSchedule, wd];

    setRecipient({
      ...recipient,
      schedule: nextSchedule,
    });
  };

  const setNineToFive = (dow) => {
    const currentSchedule = safeSchedule;
    const existing = currentSchedule.find((d) => +d?.weekday === +dow);

    const updated = {
      ...(existing || { weekday: dow }),
      weekday: dow,
      start_time: `2001-01-0${dow} 09:00:00`,
      end_time: `2001-01-0${dow} 17:00:00`,
    };

    const nextSchedule = existing
      ? currentSchedule.map((d) => (+d?.weekday === +dow ? updated : d))
      : [...currentSchedule, updated];

    setRecipient({
      ...recipient,
      schedule: nextSchedule,
    });
  };

  const applyToAllDays = (sourceDow) => {
    const currentSchedule = safeSchedule;
    const source = currentSchedule.find((d) => +d?.weekday === +sourceDow);

    if (!source) {
      UIkit.notification("Nothing to apply: this day is muted.", "warning");
      return;
    }

    const startHHMMSS = parseWd(source.start_time).isValid()
      ? parseWd(source.start_time).format("HH:mm:ss")
      : "09:00:00";

    const endHHMMSS = parseWd(source.end_time).isValid()
      ? parseWd(source.end_time).format("HH:mm:ss")
      : "17:00:00";

    const nextSchedule = DOWS.map((day) => {
      const existing = currentSchedule.find((d) => +d?.weekday === +day.id);

      return {
        ...(existing || {}),
        weekday: day.id,
        start_time: `${getDayPrefix(day.id)} ${startHHMMSS}`,
        end_time: `${getDayPrefix(day.id)} ${endHHMMSS}`,
      };
    });

    setRecipient({
      ...recipient,
      schedule: nextSchedule,
    });

    UIkit.notification("Applied schedule to all days");
  };

  const upsert = async (recipient) => {
    try {
      if (!recipient.recipient_id) {
        const recp = settings.recipients.find(
          (item) => item.email == recipient.email,
        )
        if (recp) {
          if (recp.deleted) {
            UIkit.notification("This user was previously deleted from your account. Please contact RueBaRue Support to reactivate the user.", 
              "error"
            );
            return;
          }
          UIkit.notification("Email already exists", "error");
          return false;
        }
        await createRecipient(recipient);        
        UIkit.notification("Added Successfully");
      } else {
        await updateRecipient(recipient);
        UIkit.notification("Updated Successfully");
      }
      UIkit.modal("#team-add").hide();
    } catch (error) {
      console.error("TeamAdd upsert failed:", {
        error,
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });

      if (error.response?.status === 422) {
        UIkit.notification(
          "This email is associated with another RueBaRue account. Please use a different email address or contact support for assistance.",
          "error"
        );
      } else {
        UIkit.notification(isNew ? "Add Failed" : "Update Failed", "error");
      }
    }
  }

  const handleUpdate = async (e) => {
    e.preventDefault();

    let errs = requiredFields.filter((f) => !recipient[f]);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (recipient.email && !emailRegex.test(recipient.email)) {
      errs.push("email");
    }

    if (errs.length > 0) {
      setErrors(errs);
      if (errs.includes("email") && recipient.email) {
        UIkit.notification("Please enter a valid email address", "error");
      }
      e.stopPropagation();
      return;
    }

    if (isSaving) return;

    if (hasInvalidSchedule) {
      UIkit.notification("Please fix invalid notification hours before saving.", "error");
      return;
    }

    const normalizedRecipient = {
      ...recipient,
      schedule: safeSchedule
        .slice()
        .sort((a, b) => (+a.weekday || 0) - (+b.weekday || 0)),
    };

    setIsSaving(true);
    try {
      await upsert(normalizedRecipient);
    } finally {
      setIsSaving(false);
    }
  };

  const errorClass = (key) => {
    return errors.includes(key) ? ["error"] : [];
  };

  const setAllDay = (dow) => {
    const currentSchedule = safeSchedule;
    const existing = currentSchedule.find((d) => +d?.weekday === +dow);

    const updated = {
      ...(existing || { weekday: dow }),
      weekday: dow,
      start_time: `2001-01-0${dow} 00:00:00`,
      end_time: `2001-01-0${dow} 23:59:00`,
    };

    const nextSchedule = existing
      ? currentSchedule.map((d) => (+d?.weekday === +dow ? updated : d))
      : [...currentSchedule, updated];

    setRecipient({
      ...recipient,
      schedule: nextSchedule,
    });
  };

  const toggleMute = (dow) => {
    const currentSchedule = safeSchedule;
    const exists = currentSchedule.some((d) => +d?.weekday === +dow);

    const nextSchedule = exists
      ? currentSchedule.filter((d) => +d?.weekday !== +dow)
      : [
          ...currentSchedule,
          {
            weekday: dow,
            start_time: `2001-01-0${dow} 09:00:00`,
            end_time: `2001-01-0${dow} 17:00:00`,
          },
        ];

    setRecipient({
      ...recipient,
      schedule: nextSchedule,
    });
  };

  const hasInvalidSchedule = safeSchedule.some((wd) => {
    const start = parseWd(wd.start_time);
    const end = parseWd(wd.end_time);
    return !start.isValid() || !end.isValid() || !end.isAfter(start);
  });

  const getHours = (wd) => {
    if (!wd) return "Muted";

    const start_time = parseWd(wd.start_time);
    const end_time = parseWd(wd.end_time);

    if (!start_time.isValid() || !end_time.isValid()) return "Muted";

    return `${start_time.format("h:mma")} - ${end_time.format("h:mma")}`;
  };

  return (
    <div
      id="team-add"
      className="uk-modal-full hms-modals uk-modal uk-open uk-dark"
      uk-offcanvas="flip: true; overlay: true"
    >
      <div className="uk-offcanvas-bar">
        <div>
          <button
            className="uk-offcanvas-close uk-close-large"
            type="button"
            data-uk-close
          ></button>
          <div className="uk-modal-header">
            <h2 className="uk-modal-title">{isNew ? "Add a" : "Edit"} User</h2>
          </div>
          <div className="uk-modal-body">
            <form
              className="uk-grid-small uk-margin-none-top"
              data-uk-grid
              onSubmit={handleUpdate}
            >
              <div className="uk-width-1-1 uk-grid uk-grid-margin uk-first-column">
                <div className="uk-width-1-2 uk-first-column">
                  <label className="uk-form-label">First Name*</label>
                  <div className="uk-form-controls">
                    <input
                      className={["uk-input", ...errorClass("first_name")].join(
                        " ",
                      )}
                      type="text"
                      placeholder=""
                      value={recipient.first_name}
                      onInput={(e) => handleInput("first_name", e)}
                    />
                  </div>
                </div>
                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">Last Name*</label>
                  <div className="uk-form-controls">
                    <input
                      className={["uk-input", ...errorClass("last_name")].join(
                        " ",
                      )}
                      type="text"
                      placeholder=""
                      value={recipient.last_name}
                      onInput={(e) => handleInput("last_name", e)}
                    />
                  </div>
                </div>
              </div>
              <div className="uk-width-1-1">
                <div className="uk-width-1-2 uk-first-column">
                  <label className="uk-form-label">Email*</label>
                  <div className="uk-form-controls">
                    <input
                      className={classnames(
                        ["uk-input", ...errorClass("email")].join(" "),
                        { "input-disable-status": isNew == false },
                      )}
                      type="text"
                      placeholder=""
                      value={recipient.email}
                      onInput={(e) => handleInput("email", e)}
                      disabled={!!recipient.recipient_id}
                    />
                  </div>
                </div>
              </div>
              {/*
              <ul uk-accordion="multiple: true" className="scheduler-advanced-options">
                <li>
                  <div className="uk-accordion-title" href="#">
                    Role
                  </div>
                  <div className="uk-accordion-content">
                    <ul class="uk-list">
                      <li class="role-item">
                          <input class="role-radio" type="radio" name="role" value="owner-admin" />
                          <div class="role-content">
                              <strong>Owner/Admin</strong>
                              <div class="role-description">Full access to all features, settings, billing, and user management.</div>
                          </div>
                      </li>
                      <li class="role-item">
                          <input class="role-radio" type="radio" name="role" value="manager" />
                          <div class="role-content">
                              <strong>Manager</strong>
                              <div class="role-description">Access to all Digital Guestbook and Texting features. Can manage team members except for the Owner/Admin. Cannot change billing settings.</div>
                          </div>
                      </li>
                      <li class="role-item">
                          <input class="role-radio" type="radio" name="role" value="guestbook-editor" />
                          <div class="role-content">
                              <strong>Guestbook Editor</strong>
                              <div class="role-description">Access to Digital Guestbook (Master Home Guide, Home Guide, Area Guide, Extras Guide). No access to Texting features.</div>
                          </div>
                      </li>
                      <li class="role-item">
                          <input class="role-radio" type="radio" name="role" value="messaging-agent" />
                          <div class="role-content">
                              <strong>Messaging Agent</strong>
                              <div class="role-description">Access to Texting (Automation, Alerts, Messaging, Guests, Contacts, Work Orders, Extend Stays, Surveys). No access to Digital Guestbook features.</div>
                          </div>
                      </li>
                      <li class="role-item">
                          <input class="role-radio" type="radio" name="role" value="basic-user" />
                          <div class="role-content">
                              <strong>Basic User</strong>
                              <div class="role-description">Read-only access to everything except for messaging (can send and receive messages). Cannot modify settings or manage users.</div>
                          </div>
                      </li>
                    </ul>
                  </div>
                </li>
              </ul>
               */}
              <ul uk-accordion="multiple: true" className="scheduler-advanced-options">
                <li className="uk-open">
                  <div className="uk-accordion-title">
                    Notification Schedule
                  </div>
                  <div className="uk-accordion-content">
                    {DOWS.map((day) => {
                      const dow = day.id;
                      const wd = safeSchedule.find((d) => +d?.weekday === +dow);

                      const isAllDay =
                        !!wd &&
                        wd.start_time?.includes("00:00:00") &&
                        wd.end_time?.includes("23:59:00");

                      const st = parseWd(wd?.start_time).isValid()
                        ? parseWd(wd.start_time).format("HH:mm")
                        : "09:00";

                      const et = parseWd(wd?.end_time).isValid()
                        ? parseWd(wd.end_time).format("HH:mm")
                        : "17:00";

                      return (
                        <div
                          key={dow}
                          className="uk-width-1-1 notification-timeline-box uk-margin-bottom"
                        >
                          <div className="work-day-header uk-flex uk-flex-between uk-flex-middle uk-margin-small-bottom">
                            <div className="work-day-title uk-flex uk-flex-middle" style={{ gap: 6, flexWrap: "wrap" }}>
                              <span className="uk-text-bold">{day.label}:</span>
                              <span className="uk-text-meta">{getHours(wd)}</span>
                            </div>

                            <div className="work-day-actions uk-flex uk-flex-middle" style={{ gap: 8, flexShrink: 0 }}>
                              <button
                                type="button"
                                className="download"
                                onClick={() => setNineToFive(dow)}
                                uk-tooltip="9 to 5"
                              >
                                <Image src="/images/nine-to-five.svg" />
                              </button>

                              <button
                                type="button"
                                className="download"
                                onClick={() => setAllDay(dow)}
                                uk-tooltip="All Day"
                              >
                                <Image src="/images/24-hours.svg" />
                              </button>

                              <button
                                type="button"
                                className="download"
                                onClick={() => applyToAllDays(dow)}
                                uk-tooltip="Apply to All"
                              >
                                <Image src="/images/copy-schedule.svg" />
                              </button>

                              <button
                                type="button"
                                className="download"
                                onClick={() => toggleMute(dow)}
                                uk-tooltip="Day Off"
                              >
                                <Image src="/images/day-off.svg" />
                              </button>
                            </div>
                          </div>

                          {!!wd ? (
                            <div className="uk-flex uk-flex-middle uk-flex-wrap" style={{ gap: 12 }}>
                              <div>
                                <label className="uk-form-label uk-margin-small-right">Start</label>
                                <div className="uk-inline">
                                  <div
                                    key={`st-form-${dow}-${st}`}
                                    uk-form-custom="target: > * > span:first-child"
                                  >
                                    <select
                                      value={st}
                                      onChange={(e) => setDayTime(dow, "start_time", e.target.value)}
                                    >
                                      {timeOptionsStart.map((t) => (
                                        <option key={`${dow}-st-${t}`} value={t}>
                                          {hhmmToLabel(t)}
                                        </option>
                                      ))}
                                    </select>

                                    <button
                                      className="uk-button uk-button-default"
                                      type="button"
                                      tabIndex="-1"
                                    >
                                      <span></span>
                                      <span uk-icon="icon: chevron-down"></span>
                                    </button>
                                  </div>
                                </div>
                              </div>

                              <div>
                                <label className="uk-form-label uk-margin-small-right">End</label>
                                <div className="uk-inline">
                                  <div
                                    key={`et-form-${dow}-${st}-${et}`}
                                    uk-form-custom="target: > * > span:first-child"
                                  >
                                    <select
                                      value={et}
                                      onChange={(e) => setDayTime(dow, "end_time", e.target.value)}
                                    >
                                      {getFilteredEndTimes(st).map((t) => (
                                        <option key={`${dow}-et-${t}`} value={t}>
                                          {hhmmToLabel(t)}
                                        </option>
                                      ))}
                                    </select>

                                    <button
                                      className="uk-button uk-button-default"
                                      type="button"
                                      tabIndex="-1"
                                    >
                                      <span></span>
                                      <span uk-icon="icon: chevron-down"></span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="uk-text-muted">Notifications muted for this day.</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </li>
              </ul>
              <div className="uk-width-1-1 uk-margin-top">
                <button
                  className="uk-button hms-btn hms-red-btn"
                  type="submit"
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <span uk-spinner="ratio: 0.6" className="uk-margin-small-right" />
                      Saving...
                    </>
                  ) : (
                    "Save"
                  )}
                </button>
                <button
                  className="uk-button hms-btn uk-modal-close"
                  uk-toggle="target: #team-add"
                  type="button"
                  disabled={isSaving}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamAdd;
